import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Building2,
  DollarSign,
  Users,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Percent,
  Target,
  FileText,
  CreditCard,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import apiService from '../services/api';
import { 
  CompanyAffiliateDetailResource,
  CompanyAffiliateStats,
  CompanyAffiliateHistoryResource,
  CompanyCommissionHistoryResource
} from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import ReferralAuditLogPanel from '../components/ReferralAuditLogPanel';
import AffiliateCommissionRatesPanel from '../components/AffiliateCommissionRatesPanel';
import Pagination from '../components/Pagination';

const AffiliateCompanyDetail: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState<'detail' | 'affiliate-histories' | 'commission-histories'>('detail');
  const [loading, setLoading] = useState(true);
  const [affiliateDetail, setAffiliateDetail] = useState<CompanyAffiliateDetailResource | null>(null);
  const [affiliateStats, setAffiliateStats] = useState<CompanyAffiliateStats | null>(null);
  
  // Affiliate Histories state
  const [affiliateHistories, setAffiliateHistories] = useState<CompanyAffiliateHistoryResource[]>([]);
  const [affiliateHistoriesPagination, setAffiliateHistoriesPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  });
  const [affiliateHistoriesLoading, setAffiliateHistoriesLoading] = useState(false);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  // Tab dibuka lebih dulu, baru URL-nya diisi. Membukanya setelah await
  // membuat browser menganggapnya bukan hasil klik pengguna dan memblokirnya
  // sebagai popup.
  const handleSendWhatsapp = async () => {
    if (!affiliateDetail) return;

    const tab = window.open('', '_blank');
    setWhatsappLoading(true);
    setActionMessage(null);

    try {
      const url = await apiService.getReferrerWhatsappUrl(affiliateDetail.id);
      if (tab) {
        tab.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (err: any) {
      tab?.close();
      setActionMessage(err?.response?.data?.message ?? 'Gagal menyiapkan pesan WhatsApp.');
    } finally {
      setWhatsappLoading(false);
    }
  };

  const handleAdjustPoints = async () => {
    if (!affiliateDetail) return;

    const rawAmount = window.prompt('Nominal penyesuaian (boleh negatif, dalam rupiah):');
    if (rawAmount === null) return;

    const amount = Number(rawAmount);
    if (!Number.isFinite(amount) || amount === 0) {
      setActionMessage('Nominal harus angka dan tidak boleh nol.');
      return;
    }

    // Alasan diminta terpisah dan wajib: inilah yang tersimpan di log audit dan
    // jadi satu-satunya penjelasan saat penyesuaian ini dipertanyakan.
    const reason = window.prompt('Alasan penyesuaian (wajib):');
    if (!reason || reason.trim() === '') {
      setActionMessage('Penyesuaian dibatalkan — alasan wajib diisi.');
      return;
    }

    setActionMessage(null);
    try {
      await apiService.adjustReferralPoints(affiliateDetail.id, amount, reason.trim());
      setActionMessage('Poin disesuaikan. Saldo dan log audit sudah diperbarui.');
      loadAffiliateDetail();
    } catch (err: any) {
      setActionMessage(err?.response?.data?.message ?? 'Gagal menyesuaikan Poin.');
    }
  };

  const handleResendSetPassword = async () => {
    if (!affiliateDetail) return;

    setResendLoading(true);
    setActionMessage(null);

    try {
      await apiService.resendReferrerSetPassword(affiliateDetail.id);
      setActionMessage('Tautan set password sudah dikirim ulang. Berlaku 7 hari.');
    } catch (err: any) {
      setActionMessage(err?.response?.data?.message ?? 'Gagal mengirim ulang tautan.');
    } finally {
      setResendLoading(false);
    }
  };
  
  // Commission Histories state
  const [commissionHistories, setCommissionHistories] = useState<CompanyCommissionHistoryResource[]>([]);
  const [commissionHistoriesPagination, setCommissionHistoriesPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  });
  const [commissionHistoriesLoading, setCommissionHistoriesLoading] = useState(false);

  // Load affiliate detail
  const loadAffiliateDetail = async () => {
    if (!companyId) return;
    
    setLoading(true);
    try {
      const [detailResponse, statsResponse] = await Promise.all([
        apiService.getCompanyAffiliateDetail(companyId),
        apiService.getCompanyAffiliateStats(companyId)
      ]);
      
      setAffiliateDetail(detailResponse.data);
      setAffiliateStats(statsResponse.data);
    } catch (error) {
      console.error('Error loading affiliate detail:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load affiliate histories
  const loadAffiliateHistories = async (page: number = 1) => {
    if (!companyId) return;
    
    setAffiliateHistoriesLoading(true);
    try {
      const response = await apiService.getCompanyAffiliateHistories(
        companyId,
        page,
        affiliateHistoriesPagination.per_page
      );
      setAffiliateHistories(response.data);
      setAffiliateHistoriesPagination({
        current_page: response.meta.current_page,
        last_page: response.meta.last_page,
        per_page: response.meta.per_page,
        total: response.meta.total
      });
    } catch (error) {
      console.error('Error loading affiliate histories:', error);
    } finally {
      setAffiliateHistoriesLoading(false);
    }
  };

  // Load commission histories
  const loadCommissionHistories = async (page: number = 1) => {
    if (!companyId) return;
    
    setCommissionHistoriesLoading(true);
    try {
      const response = await apiService.getCompanyCommissionHistories(
        companyId,
        page,
        commissionHistoriesPagination.per_page
      );
      setCommissionHistories(response.data);
      setCommissionHistoriesPagination({
        current_page: response.meta.current_page,
        last_page: response.meta.last_page,
        per_page: response.meta.per_page,
        total: response.meta.total
      });
    } catch (error) {
      console.error('Error loading commission histories:', error);
    } finally {
      setCommissionHistoriesLoading(false);
    }
  };

  useEffect(() => {
    loadAffiliateDetail();
  }, [companyId]);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'affiliate-histories') {
      loadAffiliateHistories();
    } else if (activeTab === 'commission-histories') {
      loadCommissionHistories();
    }
  }, [activeTab]);

  const formatCurrency = (amount: string | number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string, type: 'affiliate' | 'forwarded' | 'withdraw' = 'affiliate') => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    
    if (type === 'affiliate') {
      switch (status) {
        case 'active':
          return (
            <span className={`${baseClasses} bg-green-100 text-green-800`}>
              <CheckCircle className="w-3 h-3 mr-1" />
              Active
            </span>
          );
        case 'inactive':
          return (
            <span className={`${baseClasses} bg-red-100 text-red-800`}>
              <XCircle className="w-3 h-3 mr-1" />
              Inactive
            </span>
          );
        default:
          return (
            <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
              {status}
            </span>
          );
      }
    } else if (type === 'forwarded') {
      switch (status) {
        case 'true':
          return (
            <span className={`${baseClasses} bg-green-100 text-green-800`}>
              <CheckCircle className="w-3 h-3 mr-1" />
              Forwarded
            </span>
          );
        case 'false':
          return (
            <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
              <Clock className="w-3 h-3 mr-1" />
              Pending
            </span>
          );
        default:
          return (
            <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
              {status}
            </span>
          );
      }
    } else {
      switch (status) {
        case 'success':
          return (
            <span className={`${baseClasses} bg-green-100 text-green-800`}>
              <CheckCircle className="w-3 h-3 mr-1" />
              Success
            </span>
          );
        case 'requested':
          return (
            <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
              <Clock className="w-3 h-3 mr-1" />
              Requested
            </span>
          );
        case 'processing':
          return (
            <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
              <Clock className="w-3 h-3 mr-1" />
              Processing
            </span>
          );
        case 'rejected':
          return (
            <span className={`${baseClasses} bg-red-100 text-red-800`}>
              <XCircle className="w-3 h-3 mr-1" />
              Rejected
            </span>
          );
        default:
          return (
            <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
              {status}
            </span>
          );
      }
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!affiliateDetail || !affiliateStats) {
    return (
      <div className="text-center py-12">
        <Building2 className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Affiliate not found</h3>
        <p className="mt-1 text-sm text-gray-500">
          The affiliate company you're looking for doesn't exist.
        </p>
        <div className="mt-6">
          <button
            onClick={() => navigate('/affiliate')}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Affiliate
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/affiliate')}
              className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Affiliate
            </button>
          </div>
          <h2 className="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            {affiliateDetail.company.companyName}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {affiliateDetail.company.brandName} - Affiliate Details
          </p>
        </div>
        <div className="mt-4 md:mt-0 md:ml-4 flex gap-3">
          <button
            onClick={handleSendWhatsapp}
            disabled={whatsappLoading}
            className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
          >
            {whatsappLoading ? 'Menyiapkan...' : 'Kirim via WhatsApp'}
          </button>
          <button
            onClick={handleAdjustPoints}
            className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50"
          >
            Sesuaikan Poin
          </button>
          <button
            onClick={handleResendSetPassword}
            disabled={resendLoading}
            className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            {resendLoading ? 'Mengirim...' : 'Kirim ulang set password'}
          </button>
        </div>
      </div>

      {actionMessage && (
        <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
          {actionMessage}
        </div>
      )}

      <AffiliateCommissionRatesPanel affiliateId={affiliateDetail.id} />

      <ReferralAuditLogPanel affiliateId={affiliateDetail.id} />

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('detail')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'detail'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Detail
          </button>
          <button
            onClick={() => setActiveTab('affiliate-histories')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'affiliate-histories'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Affiliate Histories
          </button>
          <button
            onClick={() => setActiveTab('commission-histories')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'commission-histories'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CreditCard className="w-4 h-4 inline mr-2" />
            Commission Histories
          </button>
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'detail' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        {/* Total Commissions */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Commissions
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(affiliateStats.totalCommissions)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Pending Commissions */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Pending Commissions
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(affiliateStats.pendingCommissions)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Total Referrals */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Referrals
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {affiliateStats.totalReferrals}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Affiliate Details */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Affiliate Information
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Details about this company's affiliate program.
          </p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Referral Code
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                  {affiliateDetail.referralCode}
                </span>
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Persetujuan S&amp;K
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {affiliateDetail.termsVersion ? (
                  <span>
                    {affiliateDetail.termsVersion}
                    {affiliateDetail.termsAcceptedAt && ` — ${formatDate(affiliateDetail.termsAcceptedAt)}`}
                    {affiliateDetail.registeredVia && ` (${affiliateDetail.registeredVia === 'google_form' ? 'Google Form' : 'didaftarkan admin'})`}
                  </span>
                ) : (
                  <span className="text-gray-400">
                    Tidak tercatat — mendaftar sendiri sebelum program dijalankan lewat admin panel
                  </span>
                )}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Commission Type
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <div className="flex items-center">
                  {affiliateDetail.commissionType === 'percent' ? (
                    <Percent className="w-4 h-4 mr-1 text-blue-600" />
                  ) : ('')}
                  <span className="capitalize">{affiliateDetail.commissionType}</span>
                </div>
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Commission Value
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {affiliateDetail.commissionType === 'percent' 
                  ? `${affiliateDetail.commissionValue}%` 
                  : formatCurrency(affiliateDetail.commissionValue)
                }
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Min Commission
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatCurrency(affiliateDetail.minCommission)}
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Max Commission
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatCurrency(affiliateDetail.maxCommission)}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Status
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {getStatusBadge(affiliateDetail.status)}
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Marketing Program
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {affiliateDetail.kloaMarketingProgram.name}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Created At
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                  {formatDate(affiliateDetail.createdAt)}
                </div>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Company Information */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Company Information
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Basic information about the company.
          </p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Company Name
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {affiliateDetail.company.companyName}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Brand Name
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {affiliateDetail.company.brandName}
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Company Code
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                  {affiliateDetail.company.companyCode}
                </span>
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Business Type
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className="capitalize">{affiliateDetail.company.businessType}</span>
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Address
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {affiliateDetail.company.address}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Phone
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {affiliateDetail.company.companyPhone}
              </dd>
            </div>
            {affiliateDetail.company.companyAdminEmail && (
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Admin Email
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {affiliateDetail.company.companyAdminEmail}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
        </>
      )}

      {/* Affiliate Histories Tab */}
      {activeTab === 'affiliate-histories' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {affiliateHistoriesLoading ? (
            <LoadingSpinner />
          ) : affiliateHistories.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No affiliate histories</h3>
              <p className="mt-1 text-sm text-gray-500">
                No affiliate commission histories found for this company.
              </p>
            </div>
          ) : (
            <>
              <ul className="divide-y divide-gray-200">
                {affiliateHistories.map((history) => (
                  <li key={history.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <Building2 className="h-8 w-8 text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {history.subscribingCompany.companyName}
                              </p>
                              <span className="text-xs text-gray-500">
                                ({history.subscribingCompany.brandName})
                              </span>
                            </div>
                            <div className="flex items-center space-x-4 mt-1">
                              <div className="flex items-center text-xs text-gray-500">
                                <FileText className="w-3 h-3 mr-1" />
                                {history.subscriptionInvoice.invoiceNumber}
                              </div>
                              <div className="flex items-center text-xs text-gray-500">
                                <Calendar className="w-3 h-3 mr-1" />
                                {formatDate(history.subscribeAt)}
                              </div>
                              {/* <div className="flex items-center text-xs text-gray-500">
                                <span className="font-mono">
                                  {history.companyAffiliate.referralCode}
                                </span>
                              </div> */}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className="flex items-center text-sm font-medium text-gray-900">
                            {formatCurrency(history.totalCommission)}
                          </div>
                          {/* <div className="text-xs text-gray-500">
                            {history.companyAffiliate.commissionType === 'percent' 
                              ? `${history.companyAffiliate.commissionValue}%` 
                              : formatCurrency(history.companyAffiliate.commissionValue)
                            }
                          </div> */}
                        </div>
                        <div>
                          {getStatusBadge(history.isForwarded.toString(), 'forwarded')}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              
              {/* Pagination */}
              {affiliateHistories.length > 0 && (
                <div className="px-6 py-3 border-t border-gray-200">
                  <Pagination
                    currentPage={affiliateHistoriesPagination.current_page}
                    total={affiliateHistoriesPagination.total}
                    perPage={affiliateHistoriesPagination.per_page}
                    onPageChange={loadAffiliateHistories}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Commission Histories Tab */}
      {activeTab === 'commission-histories' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {commissionHistoriesLoading ? (
            <LoadingSpinner />
          ) : commissionHistories.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No commission histories</h3>
              <p className="mt-1 text-sm text-gray-500">
                No commission histories found for this company.
              </p>
            </div>
          ) : (
            <>
              <ul className="divide-y divide-gray-200">
                {commissionHistories.map((history) => (
                  <li key={history.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            {history.type === 'earn' ? (
                              <TrendingUp className="h-8 w-8 text-green-400" />
                            ) : (
                              <TrendingDown className="h-8 w-8 text-red-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {history.type === 'earn' ? 'Commission Earned' : 'Commission Withdrawal'}
                              </p>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                history.type === 'earn' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {history.type}
                              </span>
                            </div>
                            <div className="flex items-center space-x-4 mt-1">
                              {history.companyBank && (
                                <div className="flex items-center text-xs text-gray-500">
                                  <span className="font-mono">
                                    {history.companyBank.accountNumber}
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center text-xs text-gray-500">
                                <Calendar className="w-3 h-3 mr-1" />
                                {formatDate(history.createdAt)}
                              </div>
                              {history.withdrawDetail && (
                                <div className="flex items-center text-xs text-gray-500">
                                  <span className="truncate max-w-xs">
                                    {history.withdrawDetail}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className={`flex items-center text-sm font-medium ${
                            history.type === 'earn' ? 'text-green-900' : 'text-red-900'
                          }`}>
                            {formatCurrency(history.netAmount)}
                          </div>
                          {history.commissionTransferFee && (
                            <div className="text-xs text-gray-500">
                              Fee: {formatCurrency(history.commissionTransferFee)}
                            </div>
                          )}
                        </div>
                        <div>
                          {history.withdrawStatus && getStatusBadge(history.withdrawStatus, 'withdraw')}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              
              {/* Pagination */}
              {commissionHistories.length > 0 && (
                <div className="px-6 py-3 border-t border-gray-200">
                  <Pagination
                    currentPage={commissionHistoriesPagination.current_page}
                    total={commissionHistoriesPagination.total}
                    perPage={commissionHistoriesPagination.per_page}
                    onPageChange={loadCommissionHistories}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AffiliateCompanyDetail;
